import {Button, Textarea} from '@nlabs/gothamjs/components';
import {useState} from 'react';

import {fieldLabel, primaryButton, secondaryButton, textArea, textInput} from '../../theme/classes.js';

import type {Character, Project} from '@reactorbox/shared';
import type {FormEvent, ReactElement} from 'react';
import type {WorkspaceActions} from '../../state/workspaceActions.js';

interface CharacterContinuityProps {
  readonly actions: WorkspaceActions;
  readonly busy: boolean;
  readonly character: Character;
  readonly project: Project;
}

export const CharacterContinuity = ({actions, busy, character, project}: CharacterContinuityProps): ReactElement => {
  const [afterShotId, setAfterShotId] = useState('');
  const [emotion, setEmotion] = useState('');
  const [experience, setExperience] = useState('');
  const [knowledge, setKnowledge] = useState('');
  const [relationships, setRelationships] = useState('');
  const entries = character.continuity || [];
  const save = (event: FormEvent): void => {
    event.preventDefault();
    void actions.mutate(`/api/projects/${project.id}/characters/${character.id}`, {
      continuity: [...entries, {afterShotId: afterShotId || undefined, emotion, experience, knowledge, relationships}]
    }, 'Character continuity saved.', 'PATCH');
  };
  return <details className="mt-5 border-t border-white/10 pt-4">
    <summary className="cursor-pointer text-sm text-sky-200">Memory & emotions · {entries.length} entries</summary>
    <p className="mt-3 text-xs leading-6 text-white/60">Record what changes. Entries apply to later shots featuring this character; backstory applies from the beginning.</p>
    <ul className="my-4 space-y-3">{entries.map((entry) => <li className="rounded-xl bg-white/5 p-3 text-xs leading-6" key={entry.id}>
      <p className="text-sky-200">{entry.afterShotId ? `After ${project.shots.find((shot) => shot.id === entry.afterShotId)?.title || 'shot'}` : 'Backstory'}</p>
      <p>{entry.experience}</p>
      {entry.knowledge && <p>Knows: {entry.knowledge}</p>}
      {entry.emotion && <p>Feels: {entry.emotion}</p>}
      {entry.relationships && <p>Relationships: {entry.relationships}</p>}
      <Button className={`${secondaryButton} mt-2`} disabled={busy} onClick={() => void actions.mutate(`/api/projects/${project.id}/characters/${character.id}`, {continuity: entries.filter((item) => item.id !== entry.id)}, 'Continuity entry removed.', 'PATCH')} variant="outline">Remove entry</Button>
    </li>)}</ul>
    <form className="space-y-3" onSubmit={save}>
      <label className={fieldLabel}>Applies after<select className={`${textInput} mt-2`} onChange={(event) => setAfterShotId(event.target.value)} value={afterShotId}><option value="">Backstory · before the first shot</option>{project.shots.map((shot) => <option key={shot.id} value={shot.id}>{shot.title}</option>)}</select></label>
      <label className={fieldLabel}>Experience<Textarea className={`${textArea} mt-2`} maxLength={2000} onChange={(event) => setExperience(event.target.value)} required rows={2} value={experience} /></label>
      <label className={fieldLabel}>What they know<Textarea className={`${textArea} mt-2`} maxLength={1000} onChange={(event) => setKnowledge(event.target.value)} rows={2} value={knowledge} /></label>
      <label className={fieldLabel}>Emotional state<Textarea className={`${textArea} mt-2`} maxLength={1000} onChange={(event) => setEmotion(event.target.value)} rows={2} value={emotion} /></label>
      <label className={fieldLabel}>Relationships / unresolved questions<Textarea className={`${textArea} mt-2`} maxLength={1000} onChange={(event) => setRelationships(event.target.value)} rows={2} value={relationships} /></label>
      <Button className={primaryButton} disabled={busy || !experience.trim()} type="submit">Save memory</Button>
    </form>
  </details>;
};
